import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/lenis";

const PRODUCTS = [
  {
    name: "Solar Panels",
    model: "N-Type Bifacial Mono PERC — 575 Wp",
    spec: "22.8% efficiency · 25-yr warranty",
    price: "₹13,999",
    unit: "/ panel",
    tag: "Tier-1",
    img: "https://images.unsplash.com/photo-1724041875334-0a6397111c7e?auto=format&fit=crop&w=1200&q=80",
    span: "lg:col-span-4",
    h: "h-[300px] lg:h-[440px]",
  },
  {
    name: "Smart Inverters",
    model: "Dual-MPPT Hybrid — 5 kW",
    spec: "97.6% efficiency · WiFi monitoring",
    price: "₹34,999",
    unit: "onwards",
    tag: "Bestseller",
    img: "https://images.unsplash.com/photo-1780445392417-68b9dccc45f2?auto=format&fit=crop&w=1000&q=80",
    span: "lg:col-span-2",
    h: "h-[300px] lg:h-[440px]",
  },
  {
    name: "Battery Storage",
    model: "LiFePO₄ Battery Wall — 5 kWh",
    spec: "6,000 cycles · 10-yr warranty",
    price: "₹1,24,999",
    unit: "onwards",
    tag: "New",
    img: "https://images.unsplash.com/photo-1759536588260-e708d399a2ce?auto=format&fit=crop&w=1000&q=80",
    span: "lg:col-span-2",
    h: "h-[300px] lg:h-[340px]",
  },
  {
    name: "Complete Rooftop System",
    model: "3 kW Home Package — panels + inverter + mounting + install",
    spec: "Subsidy eligible up to ₹78,000",
    price: "₹1,89,000",
    unit: "all-inclusive",
    tag: "Subsidy Ready",
    img: "https://images.unsplash.com/photo-1655300256335-beef51a914fe?auto=format&fit=crop&w=1200&q=80",
    span: "lg:col-span-4",
    h: "h-[300px] lg:h-[340px]",
  },
  {
    name: "Solar Water Heaters",
    model: "ETC Solar Water Heater — 200 LPD",
    spec: "PUF insulated · rust-proof tank",
    price: "₹24,999",
    unit: "onwards",
    tag: "Best Value",
    img: "https://images.unsplash.com/photo-1723407653103-7a9c6b579acf?auto=format&fit=crop&w=1000&q=80",
    span: "lg:col-span-3",
    h: "h-[300px] lg:h-[320px]",
  },
  {
    name: "Solar Lights",
    model: "Street & Garden Lights — 20W to 120W",
    spec: "Dusk-to-dawn auto · IP66 weatherproof",
    price: "₹9,499",
    unit: "onwards",
    tag: "Plug & Play",
    img: "https://images.unsplash.com/photo-1741646557129-eeb404155d65?auto=format&fit=crop&w=1000&q=80",
    span: "lg:col-span-3",
    h: "h-[300px] lg:h-[320px]",
  },
];

const Card = ({ p, onEnquire, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative overflow-hidden rounded-3xl border border-white/10 ${p.span} ${p.h} transition-colors duration-500 hover:border-gold/40`}
    data-testid={`product-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <img
      src={p.img}
      alt={p.model}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-ink/10" />
    <div
      className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
      style={{ background: "radial-gradient(60% 50% at 50% 100%, rgba(245,158,11,0.18), transparent 70%)" }}
      aria-hidden="true"
    />

    <div className="relative flex h-full flex-col justify-between p-6 sm:p-7">
      <div className="flex items-start justify-between">
        <span className="rounded-full border border-gold/40 bg-ink/60 px-3.5 py-1.5 font-mono text-[10px] tracking-[0.2em] text-gold backdrop-blur-md">
          {p.tag.toUpperCase()}
        </span>
        <span className="rounded-full glass border border-white/10 px-4 py-2 text-right">
          <span className="font-display text-base sm:text-lg font-bold text-warm">{p.price}</span>
          <span className="ml-1.5 font-mono text-[9px] tracking-widest text-mutegray">{p.unit.toUpperCase()}</span>
        </span>
      </div>

      <div className="flex items-end justify-between gap-4">
        <div>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-warm">{p.name}</h3>
          <p className="mt-1 text-[13px] text-warm/70">{p.model}</p>
          <p className="mt-0.5 font-mono text-[10px] tracking-[0.18em] uppercase text-gold/90">{p.spec}</p>
        </div>
        <button
          type="button"
          data-testid={`product-enquire-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onEnquire(p.name)}
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-all duration-300 hover:bg-gold hover:text-ink hover:shadow-[0_0_24px_rgba(245,158,11,0.5)]"
          aria-label={`Enquire about ${p.name}`}
        >
          <ArrowUpRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  </motion.article>
);

export const Products = ({ onEnquire }) => (
  <section id="products" className="relative py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="eyebrow">01 — The Lineup</p>
          <h2 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight">
            Everything the sun <span className="text-gold-grad">can do.</span>
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-sm text-sm leading-relaxed text-mutegray"
        >
          From a single panel to a fully engineered rooftop power plant — every product is
          Tier-1 certified, DISCOM approved and installed by our own crews.
        </motion.p>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-6 gap-5" data-testid="products-bento">
        {PRODUCTS.map((p, i) => (
          <Card key={p.name} p={p} i={i} onEnquire={onEnquire} />
        ))}
      </div>
    </div>
  </section>
);

export default Products;
